import i18next from 'i18next';
import { SagaIterator } from 'redux-saga';
import {
  all,
  put,
  call,
  take,
  takeEvery,
  select,
  race,
} from 'redux-saga/effects';
import { Url, DEVIATION_PAGE_LANDING_ACTION, sanitizeUrl } from '@wix/da-url';
import {
  PapiDaSharedDeviationDelete,
  PapiDaSharedDeviationPublish,
  PapiDaSharedJournalPublish,
  PapiDaSharedDeviationUpdate,
  PapiDeviation,
  PapiRequestDaSharedDeviationDelete,
  PapiRequestDaSharedDeviationPublish,
  PapiRequestDaSharedDeviationUpdate,
  PapiUser,
  PapiRequestDaSharedAdoptableAssetCreateAndList,
  PapiDaSharedAdoptableAssetEdit,
  PapiRequestDaSharedAdoptableAssetEdit,
} from '@wix/da-papi-types';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import {
  isApiError,
  requestPuppy,
  requestNapi,
  XhrResponse,
} from '@wix/da-http-client';
import { getDeviationById } from '@wix/da-shared-react/pkg/redux/deviations/selectors';
import { getHasBeenTraded } from '@wix/da-shared-react/pkg/types/deviation/getters';
import {
  pushModal,
  confirmNo,
  confirmYes,
} from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { updateDeviationEntity } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import {
  submitDeviation,
  deleteDeviation,
  deviationSaveError,
  submitDeviationSuccess,
  deviationSaveCancel,
  publishDeviation,
  publishDeviationSuccess,
  publishDeviationError,
} from '../actions';
import { DeviationSubmitFormData } from '../types';
import { fakeDeviationResponse } from '../fake';
import { SUBMIT_POPUP_ERROR_CODE } from '../constants';

function* throwError(errorMessage: string, errorCode: SUBMIT_POPUP_ERROR_CODE) {
  // T63499: Show toaster notification unless it's a quota error
  if (errorCode !== SUBMIT_POPUP_ERROR_CODE.ITEM_COUNT_QUOTA_LIMIT_REACHED) {
    yield putErrorNotification(errorMessage);
  }
  yield put(deviationSaveError(errorMessage, errorCode));
}

function* handleSaveDeviation({
  deviationId,
  descriptionState,
  ...formData
}: DeviationSubmitFormData) {
  const url = '/deviation/update';
  const method = 'post';

  try {
    const data: PapiRequestDaSharedDeviationUpdate = {
      deviationid: `${deviationId}`,
      editorRaw: descriptionState as any,
      title: formData.title,
      is_mature: formData.is_mature,
      is_scrap: formData.is_scrap,
      allow_comments: formData.allow_comments,
      allow_free_download: formData.allow_free_download,
      add_watermark: formData.add_watermark,
      display_resolution: formData.display_resolution,
      tags: formData.tags,
      tierids: formData.tierids,
      galleryids: formData.galleryids,
      pcp_price_points: formData.pcp_price_points,
      pcp_include_original: formData.pcp_include_original,
      reset_pcp: formData.reset_pcp,
      groups: formData.groups,
      group_folders: formData.group_folders,
      noai: formData.noai,
      is_ai_generated: formData.is_ai_generated,
      notify_watchers: formData.notify_watchers,
      license_options: formData.license_options,
      location_tag: formData.location_tag,
      subject_tag_types: formData.subject_tag_types,
      subject_tags: formData.subject_tags,
      dreamsofart_prompt_hidden: formData.dreamsofart_prompt_hidden,
      oauth2_grant_type: formData.oauth2_grant_type,
      oauth2_redirect_whitelist: formData.oauth2_redirect_whitelist,
      download_url: formData.download_url,
      original_urls_whitelist: formData.original_urls_whitelist,
    };

    // using request as this uses draftJS
    return yield call(
      requestNapi,
      {
        method,
        url,
        data,
      },
      fakeDeviationResponse
    );
  } catch (e) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError'),
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_SAVE_ERROR
    );
  }
}

function* handlePublishDeviation(
  deviationId: number,
  onError?: (deviationId: number) => void
) {
  // Publish the deviation
  const data: PapiRequestDaSharedDeviationPublish = {
    stashid: deviationId,
  };
  const response: PapiDaSharedDeviationPublish = yield call(requestPuppy, {
    method: 'post',
    url: '/deviation/publish',
    data,
  });

  if (!response) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError'),
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_PUBLISH_ERROR
    );
    if (onError) {
      yield call(onError, deviationId);
    }
    return;
  }

  if (isApiError(response)) {
    // Handle api redirects
    if (response.errorCode === 302 && response.errorDetails?.redirectUrl) {
      window.location.replace(sanitizeUrl(response.errorDetails.redirectUrl));
    }

    // For the "quota limit reached" error we need a special UI
    // so we have to try our best to catch it using the error code that the backend provides
    const isQuotaLimitError = response.errorCode && response.errorCode === 1;
    yield call(
      throwError,
      response.errorDescription,
      isQuotaLimitError
        ? SUBMIT_POPUP_ERROR_CODE.ITEM_COUNT_QUOTA_LIMIT_REACHED
        : SUBMIT_POPUP_ERROR_CODE.DEVIATION_PUBLISH_ERROR
    );
    if (onError) {
      yield call(onError, deviationId);
    }
    return;
  }

  return response.deviation;
}

function* handleCreateExclusive(
  deviation: PapiDeviation,
  formData: NonNullable<DeviationSubmitFormData['createExclusive']>
) {
  const payload: PapiRequestDaSharedAdoptableAssetCreateAndList = {
    deviationid: deviation.deviationId,
    necoid: Number(formData.necoid),
    source_url: formData.source_url,
    purchase_price: Number(formData.purchase_price),
    offer: Number(formData.offer),
    offer_expiration: Number(formData.duration),
    offer_delayed_start: formData.offer_delayed_start,
    early_access: formData.early_access,
    allow_resale: formData.allow_resale,
    include_original: formData.include_original,
  };

  const result: XhrResponse<PapiDaSharedAdoptableAssetEdit> = yield call(
    requestPuppy,
    {
      method: 'POST',
      url: 'adoptable/asset/create_and_list',
      data: payload,
    }
  );

  if (!result || isApiError(result)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError'),
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_SAVE_ERROR
    );
    return;
  }

  return result;
}

function* handleEditExclusive(
  deviation: PapiDeviation,
  sourceUrl: string,
  includeOriginal?: boolean
) {
  const payload: PapiRequestDaSharedAdoptableAssetEdit = {
    deviationid: deviation.deviationId,
    source_url: sourceUrl,
    include_original: includeOriginal,
  };

  const result: XhrResponse<PapiDaSharedAdoptableAssetEdit> = yield call(
    requestPuppy,
    {
      method: 'POST',
      url: 'adoptable/asset/edit',
      data: payload,
    }
  );

  if (!result || isApiError(result)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError'),
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_SAVE_ERROR
    );
    return;
  }

  return result;
}

function* submitDeviationSaga(
  action: ReturnType<typeof submitDeviation>
): SagaIterator {
  const {
    contextId,
    formData,
    doPublish,
    doRedirect = true,
    onSuccess,
    onError,
  } = action.payload;
  const { tierids = [] } = formData;
  let { deviationId } = formData;

  const deviationBeforeUpdate: PapiDeviation = deviationId
    ? yield select(getDeviationById, deviationId)
    : undefined;

  if (
    deviationBeforeUpdate &&
    doPublish &&
    !deviationBeforeUpdate.isPublished &&
    getHasBeenTraded(deviationBeforeUpdate)
  ) {
    yield put(pushModal(ModalType.PRIVATE_SELL_CONFIRMATION, {}));

    const { no } = yield race({
      yes: take(confirmYes),
      no: take(confirmNo),
    });

    if (no) {
      yield put(deviationSaveCancel());
      return;
    }
  }

  const updateResponse: PapiDaSharedDeviationUpdate = yield call(
    handleSaveDeviation,
    formData
  );

  if (
    !updateResponse ||
    isApiError(updateResponse) ||
    !updateResponse.deviationId
  ) {
    const errorMessage =
      (updateResponse as any)?.errorDescription ??
      i18next.t('deviationSubmit.error.writeDeviationServerError');
    yield call(
      throwError,
      errorMessage,
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_SAVE_ERROR
    );
    if (onError) {
      yield call(onError, deviationId);
    }
    return;
  }
  deviationId = updateResponse.deviationId;
  const deviation: PapiDeviation = yield select(getDeviationById, deviationId);

  if (formData.createExclusive) {
    const result = yield call(
      handleCreateExclusive,
      deviation,
      formData.createExclusive
    );
    if (!result) {
      return;
    }
  }
  if (formData.editExclusive) {
    const result = yield call(
      handleEditExclusive,
      deviation,
      formData.editExclusive.source_url,
      formData.editExclusive.include_original
    );
    if (!result) {
      return;
    }
  }

  /**
   * Some operations require updated deviation data from the response of a publish request, so we'll bookmark it.
   *
   * > e.g. the "share to post" in dreamup's submit process will use this data to embed the deviation into a post
   */
  let updatedDeviation: PapiDeviation | undefined;
  if (doPublish && !deviation?.isPublished) {
    updatedDeviation = yield call(handlePublishDeviation, deviationId, onError);
  }

  if (!doRedirect) {
    const isPublished = deviation.isPublished || doPublish;
    // allow the notification popups to be displayed having a fresh title if it was changed
    deviation.title = formData.title || deviation.title;
    yield put(
      submitDeviationSuccess({
        contextId,
        deviation: updatedDeviation ?? deviation,
        isPublished,
      })
    );
    if (updatedDeviation) {
      yield put(updateDeviationEntity(updatedDeviation));
    }
  }

  // optional callback when everything went well
  if (onSuccess) {
    yield call(onSuccess, deviationId);
  }

  if (doRedirect) {
    const { username } = yield select(getCurrentUser);
    if (Array.isArray(tierids) && tierids.length > 0) {
      // If submitted to a tier, redirect to a tier
      const tierDeviationId = tierids[0];
      window.location.href = Url.userSubscriptionsPath({
        tierContentType: 'deviations',
        tierDeviationId,
        username,
      });
      return;
    }

    // otherwise redirect to the deviation (go to stash if unpublished)
    if (!deviation.isPublished && !doPublish && deviation.url) {
      window.location.href = sanitizeUrl(deviation.url);
    } else {
      window.location.href = Url.deviationLink(
        deviationId,
        username,
        undefined,
        undefined,
        DEVIATION_PAGE_LANDING_ACTION.PUBLISHED
      );
    }
  }
}

function* deleteDeviationSaga(
  action: ReturnType<typeof deleteDeviation>
): SagaIterator {
  const deviationid = action?.payload?.deviation?.deviationId;

  if (!deviationid) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError'),
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_DELETE_ERROR
    );
    return;
  }

  const data: PapiRequestDaSharedDeviationDelete = {
    deviationid,
  };
  const response: PapiDaSharedDeviationDelete = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/deviation/delete`,
      data,
    },
    fakeDeviationResponse
  );

  if (!response) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError'),
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_DELETE_ERROR
    );
    return;
  }

  if (isApiError(response)) {
    yield call(
      throwError,
      response.errorDescription,
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_DELETE_ERROR
    );
    return;
  }

  const currentUser: PapiUser = yield select(getCurrentUser);
  window.location.href = Url.userSubscriptionsPath({
    username: currentUser.username,
  });
}

function* publishDeviationSaga({
  payload: { deviation, onSuccess, onError },
}: ReturnType<typeof publishDeviation>) {
  const isJournal = getDeviationType(deviation) === DeviationTypes.JOURNAL;
  const data = isJournal
    ? { deviationid: deviation.deviationId, featured: false }
    : { stashid: deviation.deviationId };
  const url = isJournal ? '/journal/publish' : '/deviation/publish';

  const response: PapiDaSharedDeviationPublish | PapiDaSharedJournalPublish =
    yield call(requestPuppy, {
      method: 'post',
      url,
      data,
    });

  if (!response) {
    yield put(publishDeviationError(deviation, response));
    if (onError) {
      onError(response);
    }
    return;
  }

  if (isApiError(response)) {
    // Handle api redirects
    if (response.errorCode === 302 && response.errorDetails?.redirectUrl) {
      window.location.replace(sanitizeUrl(response.errorDetails.redirectUrl));
    }

    yield put(publishDeviationError(deviation, response));
    if (onError) {
      onError(response);
    }
    return;
  }

  yield put(publishDeviationSuccess(response.deviation));
  if (onSuccess) {
    onSuccess(response.deviation);
  }
}

export default function* deviationsSaga() {
  yield all([
    takeEvery(submitDeviation, submitDeviationSaga),
    takeEvery(deleteDeviation, deleteDeviationSaga),
    takeEvery(publishDeviation, publishDeviationSaga),
  ]);
}
